/**
 * Labstep
 *
 * @module core/Select/Search
 * @desc Async Search Select Container
 */

import { useHasAccessCreate } from 'labstep-web/components/Entity/Can/hooks';
import { EntityCreateContainer } from 'labstep-web/containers/Entity/Create';
import { Flex } from 'labstep-web/core/Flex';
import { Icon } from 'labstep-web/core/Icon';
import { Select } from 'labstep-web/core/Select';
import { SearchHOC } from 'labstep-web/hoc/Search';
import { Folder } from 'labstep-web/models/folder.model';
import { Group } from 'labstep-web/models/group.model';
import { ResourceLocation } from 'labstep-web/models/resource-location.model';
import { getHumanReadableEntityName } from 'labstep-web/services/i18n.service';
import { objectOrFunction } from 'labstep-web/services/react.service';
import { flattenObject } from 'labstep-web/services/utils.service';
import { denormalizeNormalizedEntity } from 'labstep-web/state/selectors/entity';
import { mergeStatuses } from 'labstep-web/state/selectors/helpers';
import React from 'react';
import Creatable from 'react-select/creatable';
import { floatingStyles } from './styles';
import styles from './styles.module.scss';
import type {
  ICreateLabelProps,
  ISearchSelectState,
  SearchSelectContainerProps,
  SearchSelectProps,
} from './types';
import {
  getPlaceholder,
  isValidNewOption,
  noOptionsMessage,
} from './utils';

const debounceTime = 500;

export const CreateLabel: React.FC<ICreateLabelProps> = ({
  createLabel,
  createIcon,
  entityPrefix,
  entityName,
  inputValue,
  isTemplate,
}) => {
  const createLabelValue = `: "${
    entityPrefix
      ? entityPrefix + (inputValue ? ` - ${inputValue}` : '')
      : inputValue || ''
  }"`;

  return (
    <span>
      {createIcon && <Icon name={createIcon} />}
      {createLabel ||
        `Create new ${getHumanReadableEntityName(
          entityName,
          false,
          false,
          isTemplate,
        )}`}
      {createLabelValue}
    </span>
  );
};

export class SearchSelect extends React.Component<
  SearchSelectProps,
  ISearchSelectState
> {
  searchSelectTimeout: NodeJS.Timeout | null = null;

  static defaultProps = {
    searchKey: 'search_query',
    valueKey: 'id',
    labelKey: 'displayName',
    createKey: 'name',
  };

  constructor(props: SearchSelectProps) {
    super(props);
    this.state = {
      value: '',
      debouncing: false,
      hasMounted: false,
      input: '',
      optionsToFilterOut: [],
    };
    this.handleChange = this.handleChange.bind(this);
    this.handleInputChange = this.handleInputChange.bind(this);
    this.handleCreate = this.handleCreate.bind(this);
  }

  componentDidMount() {
    setTimeout(() => {
      this.setState({ hasMounted: true });
    }, 10);
  }

  handleChange(selectedOption: any) {
    const { onChange, denormalized, removeSelectedOption } =
      this.props;

    const { input } = this.state;

    if (removeSelectedOption) {
      this.setState((prevState) => {
        return {
          optionsToFilterOut: [
            ...prevState.optionsToFilterOut,
            selectedOption.id,
          ],
        };
      });
    } else {
      this.setState({ value: selectedOption });
    }

    if (onChange) {
      const option = denormalized
        ? flattenObject(selectedOption)
        : selectedOption;
      onChange(option, input);
    }
  }

  handleInputChange(input: string) {
    const {
      searchKey = SearchSelect.defaultProps.searchKey,
      setParams,
      minimumCharacters,
    } = this.props;

    if (minimumCharacters && input.length < minimumCharacters) {
      return;
    }

    const param = input === '' ? undefined : input;

    if (this.searchSelectTimeout) {
      clearTimeout(this.searchSelectTimeout);
    }
    this.setState({ debouncing: true, input });
    this.searchSelectTimeout = setTimeout(() => {
      this.setState({ debouncing: false });
      setParams({ [searchKey]: param });
    }, debounceTime);
  }

  handleCreate(input: string) {
    const {
      create,
      createKey = SearchSelect.defaultProps.createKey,
      onCreateOptions = {},
      entityName,
      refreshUuid,
      setOptions,
      createBody = {},
      skipCreateKey,
      allowCreateWithEmptyInput,
    } = this.props;

    let body = createBody;
    if (
      !skipCreateKey &&
      !(input.length < 1 && allowCreateWithEmptyInput)
    ) {
      body = { ...createBody, [createKey]: input };
    }

    create(body as any, {
      toast: true,
      onSuccess: ({ response }) => {
        if (onCreateOptions.onSuccess) {
          (onCreateOptions.onSuccess as any)({ response });
        }
        refreshUuid();
        const entity = denormalizeNormalizedEntity(
          response as any,
          entityName,
        );

        const option = setOptions ? setOptions([entity])[0] : entity;
        this.handleChange(option);
      },
    });
  }

  render() {
    const {
      status,
      entities,
      setOptions,
      valueKey = SearchSelect.defaultProps.valueKey,
      labelKey = SearchSelect.defaultProps.labelKey,
      searchKey = SearchSelect.defaultProps.searchKey,
      setParams,
      searchParams,
      onChange,
      creatable,
      createIcon,
      createEntityPrefix,
      createStatus,
      denormalized,
      entityName,
      entityNameForText,
      noEntitiesMessage,
      create,
      createKey,
      value,
      minimumCharacters,
      createLabel,
      caseSensitive,
      allowCreateWithEmptyInput,
      placeholder,
      isTemplate,
      usePortal,
      isFloating,
      isClearable,
      keepMenuOpen,
      getOptionActions,
      dataCy,
      ...rest
    } = this.props;

    const { debouncing, hasMounted, optionsToFilterOut } = this.state;

    const Component = creatable ? Creatable : Select;

    // setOptions can be optionally passed to take over the renderedOptions
    let renderedOptions = setOptions
      ? setOptions(entities)
      : entities;
    renderedOptions = renderedOptions.filter(
      (option) => !optionsToFilterOut.includes(option.id),
    );
    const isLoading =
      mergeStatuses([status, createStatus]).isFetching || debouncing;
    // Set results to empty array if minimumCharacters is passed
    if (
      (minimumCharacters &&
        (!searchParams[searchKey] ||
          (searchParams[searchKey] as string).length <
            minimumCharacters)) ||
      (debouncing && !keepMenuOpen)
    ) {
      renderedOptions = [];
    }

    // If we require form field then let it take control of the value
    const finalValue =
      rest.fieldType || value !== undefined
        ? value
        : this.state.value;

    const isDisabled = createStatus && createStatus.isFetching;

    const setEntityNameForText = entityNameForText || entityName;

    const portalProps = usePortal
      ? {
          menuPortalTarget: document.body,
          menuShouldBlockScroll: true,
          styles: {
            menuPortal: (base: any) => ({
              ...base,
              zIndex: 9999,
            }),
          },
        }
      : {};

    const menuOpenProps = keepMenuOpen
      ? {
          menuIsOpen: true,
          blurInputOnSelect: false,
          closeMenuOnSelect: false,
        }
      : {};

    return (
      <Component
        data-cy={dataCy}
        className="Select-Search-Container"
        classNamePrefix="Select-Search"
        getNewOptionData={(inputValue) => ({
          [valueKey]: inputValue,
          [labelKey]: (
            <CreateLabel
              createLabel={createLabel}
              createIcon={createIcon}
              entityPrefix={createEntityPrefix}
              entityName={setEntityNameForText}
              inputValue={inputValue}
              isTemplate={isTemplate}
            />
          ),
          // TODO: remove once react-select fixes this issue
          // https://github.com/JedWatson/react-select/issues/3988#issuecomment-670794396
          __isNew__: true,
          isEqual: () => false,
        })}
        createOptionPosition="first"
        isValidNewOption={(inputValue, selectValue, selectOptions) =>
          isValidNewOption(
            inputValue,
            selectValue,
            selectOptions,
            labelKey,
            caseSensitive,
            allowCreateWithEmptyInput,
            creatable,
          )
        }
        onCreateOption={this.handleCreate}
        placeholder={
          placeholder ||
          getPlaceholder(
            setEntityNameForText,
            creatable,
            !!isTemplate,
          )
        }
        getOptionLabel={(option) => option[labelKey]}
        getOptionValue={(option) => option[valueKey]}
        formatOptionLabel={(option) => {
          let optionName = option[labelKey];
          if (labelKey === 'displayName') {
            if (option.custom_identifier) {
              optionName = option.name
                ? `${option.custom_identifier} - ${option.name}`
                : option.custom_identifier;
            } else {
              optionName = option.name || option.displayName;
            }
          }
          return (
            <Flex
              style={{ alignItems: 'baseline' }}
              spacing="between"
              className={styles.optionLabel}
              data-cy={`${dataCy || 'core/Select/Search'}:optionLabel`}
            >
              <div>
                {option.icon && (
                  <Icon
                    data-cy={`${dataCy || 'core/Select/Search'}:option`}
                    name={option.icon}
                  />
                )}
                {optionName}
              </div>
              <div>
                {getOptionActions &&
                  option.id &&
                  getOptionActions(option)}
              </div>
            </Flex>
          );
        }}
        options={renderedOptions}
        isLoading={isLoading}
        isDisabled={isDisabled || false}
        noOptionsMessage={() =>
          noOptionsMessage(
            setEntityNameForText,
            searchParams,
            noEntitiesMessage,
            creatable,
            minimumCharacters,
            searchKey,
            !!isTemplate,
          )
        }
        value={finalValue}
        // Prevents select component to do internal searching
        filterOption={() => true}
        isClearable={isClearable !== false}
        menuPlacement="auto"
        // hasMounted need because of https://github.com/Labstep/web/issues/7065
        openMenuOnFocus={
          !hasMounted && (rest.autoFocus || keepMenuOpen)
        }
        styles={isFloating ? floatingStyles : {}}
        {...menuOpenProps}
        {...portalProps}
        {...rest}
        onInputChange={this.handleInputChange}
        onChange={this.handleChange}
        keepMenuOpen={keepMenuOpen}
      />
    );
  }
}

export const SearchSelectContainer: React.FC<
  SearchSelectContainerProps
> = ({
  entityName,
  params,
  extraParams,
  creatable,
  entityNameForText,
  isCursor,
  usePartialSerializer,
  usePostFilter,
  dataCy,
  createBody,
  ...rest
}) => {
  let entityNameForPermission = entityName;
  if (createBody?.is_template) {
    entityNameForPermission = `${entityName}_template`;
  }

  if (entityName === Folder.entityName) {
    entityNameForPermission = `${Folder.entityName}_${createBody?.type}`;
  }

  const canCreateEntity = useHasAccessCreate(entityNameForPermission);
  let isCreatable = false;
  if (creatable) {
    if (entityName === Group.entityName) {
      isCreatable = true;
    } else {
      isCreatable = canCreateEntity;
    }
  }
  if (usePartialSerializer) {
    extraParams = {
      ...extraParams,
      serializerGroups: 'paginator,partial',
    };
  }

  const allParams = {
    ...(!usePostFilter && { sort: '-score' }),
    skip_total: 1,
    ...params,
    ...extraParams,
  };
  if (entityName === ResourceLocation.entityName) {
    delete allParams.sort;
  }

  return (
    <SearchHOC
      entityName={entityName}
      params={allParams}
      isCursor={isCursor}
      usePostFilter={usePostFilter}
    >
      {({ status, entities, searchParams, setParams }) => (
        <EntityCreateContainer entityName={entityName}>
          {({ create, status: createStatus, refreshUuid }) => (
            <>
              <SearchSelect
                data-cy={dataCy || 'core/Select/Search'}
                // Needed so that it doesn't submit on backspace
                backspaceRemovesValue={false}
                create={create}
                createStatus={createStatus}
                entityName={entityName}
                entityNameForText={entityNameForText}
                entities={entities}
                status={status}
                refreshUuid={refreshUuid}
                setParams={setParams}
                searchParams={searchParams}
                creatable={isCreatable}
                isTemplate={
                  !!objectOrFunction(allParams, {})?.is_template
                }
                createBody={createBody}
                {...rest}
              />
            </>
          )}
        </EntityCreateContainer>
      )}
    </SearchHOC>
  );
};
